/**
 * Init/load helpers for usePartyDialogEffects.
 * Extracted to keep the main hook and edit-mode initializer under the line/complexity limits.
 */

import type { Address, Party, PartyType } from '@/gen/proto';

import { EMPTY_ADDRESS, EMPTY_FORM_DATA, parsePhoneString } from './party-dialog-helpers';
import { getEntity, type Entity } from '../../../services/entity-service';
import { getCountries, getCountryByCode, getStates } from '../../../services/geolocation-service';
import { getNextPartyCode, fetchNextPartyCode } from '../../../services/party-service';
import { partyTypeToRoles } from '../../../utils/billing-helpers';

import type { PartyFormData } from './party-dialog-helpers';

type FormSetter = React.Dispatch<React.SetStateAction<PartyFormData>>;

export async function resolveAddressCountry(address: Partial<Address>): Promise<Partial<Address>> {
  if (address.countryCode) {
    const c = await getCountryByCode(address.countryCode);
    if (c) { return { ...address, country: c.name }; }
    return address;
  }
  if (address.country) {
    const all = await getCountries();
    const match = all.find(c => c.name.toLowerCase() === address.country?.toLowerCase());
    if (match) { return { ...address, country: match.name, countryCode: match.identifier }; }
    return { ...address, countryCode: 'IN' };
  }
  return { ...address, countryCode: 'IN' };
}

async function preloadStates(billingAddress: Partial<Address>, shippingAddress: Partial<Address> | undefined): Promise<void> {
  try {
    const countriesToLoad = new Set<string>();
    if (billingAddress.countryCode) { countriesToLoad.add(billingAddress.countryCode); }
    if (shippingAddress?.countryCode && shippingAddress.countryCode !== billingAddress.countryCode) {
      countriesToLoad.add(shippingAddress.countryCode);
    }
    if (countriesToLoad.size > 0) {
      await Promise.all(Array.from(countriesToLoad).map(cc => getStates(cc)));
    }
  } catch { /* continue */ }
}

function extractBusinessIdentifiers(editingParty: Party): Record<string, string> {
  const fields: Record<string, string> = {};
  if (editingParty.businessIdentifiers && Array.isArray(editingParty.businessIdentifiers)) {
    for (const id of editingParty.businessIdentifiers as { type: string; value: string }[]) {
      fields[id.type] = id.value;
    }
  }
  if (editingParty.gstin) { fields.gstin = editingParty.gstin; }
  return fields;
}

interface ContactFields {
  email: string;
  phone: ReturnType<typeof parsePhoneString>;
  mobile: ReturnType<typeof parsePhoneString>;
  website: string;
}

function pickContactFields(editingParty: Party): ContactFields {
  return {
    email: editingParty.contact?.email ?? '',
    phone: parsePhoneString(editingParty.contact?.phone),
    mobile: parsePhoneString(editingParty.contact?.mobile),
    website: editingParty.contact?.website ?? '',
  };
}

function pickRoles(editingParty: Party): Party['roles'] {
  return (editingParty.roles && editingParty.roles.length > 0)
    ? editingParty.roles
    : partyTypeToRoles(editingParty.partyType);
}

function buildEditFormData(editingParty: Party, billingAddress: Partial<Address>): PartyFormData {
  const businessIdentifierFields = extractBusinessIdentifiers(editingParty);
  const contact = pickContactFields(editingParty);
  return {
    entityId: editingParty.entityId,
    entityName: editingParty.name,
    ...businessIdentifierFields,
    gstin: businessIdentifierFields.gstin ?? '',
    pan: businessIdentifierFields.pan ?? '',
    cin: businessIdentifierFields.cin ?? '',
    tan: businessIdentifierFields.tan ?? '',
    ...contact,
    addresses: [],
    billingAddress,
    billingAddressType: 'office',
    code: editingParty.code,
    tradeName: editingParty.tradeName ?? '',
    partyType: editingParty.partyType,
    roles: pickRoles(editingParty),
    gstRegistrationType: editingParty.gstRegistrationType,
    bankAccounts: [],
    creditDays: editingParty.creditDays,
    creditLimit: Math.round(Number(editingParty.creditLimit) / 100),
    defaultCurrency: editingParty.defaultCurrency,
    companyVendorCode: editingParty.companyVendorCode ?? '',
  } as unknown as PartyFormData;
}

function loadBankAndAddressesFromEntity(entityId: string, setFormData: FormSetter): void {
  void getEntity(entityId).then(result => {
    if (!result.data) { return; }
    const updates: Record<string, unknown> = {};
    if (result.data.bankAccounts) { updates.bankAccounts = result.data.bankAccounts; }
    if (result.data.addresses?.length) { updates.addresses = result.data.addresses; }
    setFormData(prev => ({ ...prev, ...updates }));
  });
}

export async function initializeEditMode(editingParty: Party, setFormData: FormSetter): Promise<void> {
  let billingAddress: Partial<Address> = editingParty.billingAddress
    ? { ...editingParty.billingAddress }
    : { ...EMPTY_ADDRESS };
  let shippingAddress: Partial<Address> | undefined = editingParty.shippingAddress
    ? { ...editingParty.shippingAddress }
    : undefined;

  billingAddress = await resolveAddressCountry(billingAddress);
  if (shippingAddress) { shippingAddress = await resolveAddressCountry(shippingAddress); }

  await preloadStates(billingAddress, shippingAddress);

  setFormData(buildEditFormData(editingParty, billingAddress));

  if (editingParty.entityId) {
    loadBankAndAddressesFromEntity(editingParty.entityId, setFormData);
  }
}

interface InitCreateModeArgs {
  partyType: PartyType;
  existingPartyCodes: string[];
  baseCurrency: string;
  loadFromLocalStorage: () => PartyFormData | null;
  setFormData: FormSetter;
  orgId: string;
  ledgerId: string;
}

export function initializeCreateMode(args: InitCreateModeArgs): void {
  const {
    partyType, existingPartyCodes, baseCurrency, loadFromLocalStorage, setFormData, orgId, ledgerId,
  } = args;
  const savedDraft = loadFromLocalStorage();
  if (savedDraft?.code && savedDraft.code.trim() !== '') {
    setFormData({ ...savedDraft, addresses: savedDraft.addresses ?? [], bankAccounts: savedDraft.bankAccounts ?? [] });
    return;
  }
  const roles = partyTypeToRoles(partyType);
  // Instant placeholder so the field isn't briefly empty; corrected by the server below.
  const fallbackCode = getNextPartyCode(roles, existingPartyCodes);
  const newFormData = savedDraft
    ? { ...savedDraft, code: fallbackCode, addresses: savedDraft.addresses ?? [], bankAccounts: savedDraft.bankAccounts ?? [] }
    : { ...EMPTY_FORM_DATA, partyType, roles, defaultCurrency: baseCurrency, code: fallbackCode };
  setFormData(newFormData);

  // Authoritative next code from the server (SQL-computed, not a client list scan).
  // Only overrides the prefilled value if the user hasn't edited it meanwhile.
  if (orgId && ledgerId) {
    void fetchNextPartyCode(orgId, ledgerId, roles).then(res => {
      if (res.data) {
        const serverCode = res.data;
        setFormData(prev => (prev.code === fallbackCode ? { ...prev, code: serverCode } : prev));
      }
    });
  }
}

export interface InitFormParams {
  editingParty?: Party | null;
  partyType: PartyType;
  existingPartyCodes: string[];
  baseCurrency: string;
  orgId: string;
  ledgerId: string;
  loadFromLocalStorage: () => PartyFormData | null;
  setFormData: FormSetter;
  setDuplicateEntities: (entities: Entity[]) => void;
  setDuplicateEntitiesParties: (map: Map<string, Party[]>) => void;
  setSelectedEntity: (entity: Entity | null) => void;
  setShowDuplicateWarning: (v: boolean) => void;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export async function initializeForm(params: InitFormParams): Promise<void> {
  const {
    editingParty, partyType, existingPartyCodes, baseCurrency, orgId, ledgerId,
    loadFromLocalStorage, setFormData, setDuplicateEntities,
    setDuplicateEntitiesParties, setSelectedEntity, setShowDuplicateWarning, setErrors,
  } = params;

  if (editingParty) {
    await initializeEditMode(editingParty, setFormData);
  } else {
    initializeCreateMode({ partyType, existingPartyCodes, baseCurrency, loadFromLocalStorage, setFormData, orgId, ledgerId });
  }

  setDuplicateEntities([]);
  setDuplicateEntitiesParties(new Map());
  setSelectedEntity(null);
  setShowDuplicateWarning(false);
  setErrors({});
}

export async function fetchEntityBusinessIdentifiers(entityId: string, setFormData: FormSetter): Promise<void> {
  try {
    const result = await getEntity(entityId);
    if (result.data) {
      const updates: Record<string, string> = {};
      for (const id of result.data.businessIdentifiers ?? []) {
        if (id.type && id.value) { updates[id.type] = id.value; }
      }
      setFormData(prev => ({ ...prev, ...updates }));
    }
  } catch { /* ignore */ }
}
